'use client'

import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { buildLocalizedPath, stripPathForLocaleSwitch } from '@/modules/languages/lib/localizedPath'
import type { PublicLanguageOption } from './LanguageSwitcher.client'

const COOKIE_NAME = 'lang_pref'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

/** Strings do banner já no idioma alvo (espelha BannerStrings de PublicLanguageChrome). */
type BannerStrings = { message: string; cta: string; dismiss: string; regionAria: string }

type Props = {
  defaultLocale: string
  currentLocale: string
  languages: PublicLanguageOption[]
  browserDetection?: string
  /** Mapa `code → strings no idioma alvo`; quando ausente, cai no i18n da página (PT). */
  bannerStrings?: Record<string, BannerStrings>
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax`
}

function mapBrowserLang(): string | null {
  const raw = typeof navigator !== 'undefined' ? navigator.language : ''
  if (!raw) return null
  const base = raw.split('-')[0]?.toLowerCase()
  return base || null
}

export function LanguageSuggestBanner({
  defaultLocale,
  currentLocale,
  languages,
  browserDetection = 'banner',
  bannerStrings,
}: Props): React.ReactElement | null {
  const t = useTranslations('LanguageBanner')
  const pathname = usePathname() || '/'
  const [suggestion, setSuggestion] = useState<PublicLanguageOption | null>(null)

  useEffect(() => {
    if (browserDetection !== 'banner') return
    if (readCookie(COOKIE_NAME)) return

    const browserBase = mapBrowserLang()
    if (!browserBase || browserBase === currentLocale.split('-')[0]?.toLowerCase()) return

    const match = languages.find(
      (l) =>
        l.code.toLowerCase() === browserBase ||
        l.code.toLowerCase().startsWith(`${browserBase}-`),
    )
    if (!match || match.code === currentLocale) return

    // Detecção depende de document.cookie/navigator.language (só pós-hidratação):
    // sincronização legítima com sistema externo; um único setState aqui.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSuggestion(match)
  }, [browserDetection, currentLocale, languages])

  if (!suggestion) return null

  const basePath = stripPathForLocaleSwitch(pathname)
  const href = buildLocalizedPath(basePath, suggestion.code, defaultLocale)

  // Strings NO IDIOMA ALVO (ex.: navegador EN → banner em inglês); fallback p/ o i18n da página (PT).
  const s = bannerStrings?.[suggestion.code]
  const message = s?.message || t('message', { language: suggestion.label })
  const cta = s?.cta || t('cta', { language: suggestion.label })
  const dismiss = s?.dismiss || t('dismiss')
  const regionAria = s?.regionAria || t('regionAria')

  return (
    <div
      className="border-b border-border bg-muted/80 px-4 py-2 text-sm"
      role="region"
      aria-label={regionAria}
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-3">
        <span>{message}</span>
        <Button size="sm" asChild>
          <a href={href} onClick={() => writeCookie(COOKIE_NAME, suggestion.code)}>
            {cta}
          </a>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          type="button"
          onClick={() => {
            writeCookie(COOKIE_NAME, 'dismissed')
            setSuggestion(null)
          }}
        >
          {dismiss}
        </Button>
      </div>
    </div>
  )
}
